import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, formatErr } from "../../lib/api";
import { Loading, PageHeader, StatusPill } from "../../components/Shared";

const STATUSES = ["open", "assigned", "in_progress", "waiting", "resolved", "closed"];

export default function Cases() {
  const [cases, setCases] = useState(null);
  const [families, setFamilies] = useState([]);
  const [form, setForm] = useState({ title: "", family_id: "", priority: "normal" });
  const [showForm, setShowForm] = useState(false);
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState("");
  const load = () => api.get("/admin/cases").then((r) => setCases(r.data));
  useEffect(() => {
    load().catch(() => setCases([]));
    api.get("/admin/customers").then((r) => setFamilies(r.data.map((x) => ({ id: x.family.id, name: x.family.name })))).catch(() => {});
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/cases", form); toast.success("Case created"); setShowForm(false); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };
  const setStatus = async (id, status) => { await api.patch(`/admin/cases/${id}`, { status }); load(); };
  const addNote = async (id) => {
    await api.patch(`/admin/cases/${id}`, { note }); setNote(""); setNoteFor(null); load();
  };
  if (!cases) return <Loading />;
  return (
    <div data-testid="admin-cases">
      <PageHeader title="Cases & Tickets" sub="Every customer issue is trackable until closure."
        actions={<button onClick={() => setShowForm(!showForm)} className="p1-btn-primary !px-5 !py-2.5" data-testid="new-case-btn"><Plus className="h-4 w-4" /> New Case</button>} />
      {showForm && (
        <form onSubmit={submit} className="p1-card mb-6 grid gap-4 p-5 sm:grid-cols-3" data-testid="case-form">
          <input className="p1-input" placeholder="Case title *" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="case-title" />
          <select className="p1-input" value={form.family_id} onChange={(e) => setForm({ ...form, family_id: e.target.value })} data-testid="case-family">
            <option value="">Select family</option>
            {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <button className="p1-btn-primary !px-5 !py-2.5" data-testid="case-submit">Create</button>
        </form>
      )}
      <div className="space-y-3">
        {cases.map((c) => (
          <div key={c.id} className="p1-card p-5" data-testid={`case-row-${c.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-heading font-semibold text-stone-900">{c.title}</p>
                <p className="text-xs text-stone-400">{c.family_name} · opened {fmtDate(c.created_at)}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusPill status={c.status} />
                <select className="rounded-lg border border-stone-200 px-2 py-1 text-xs" value={c.status} onChange={(e) => setStatus(c.id, e.target.value)} data-testid={`case-status-${c.id}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                <button onClick={() => setNoteFor(noteFor === c.id ? null : c.id)} className="text-xs font-semibold text-emerald-900" data-testid={`case-note-toggle-${c.id}`}>Note</button>
              </div>
            </div>
            {(c.notes || []).length > 0 && (
              <ul className="mt-3 space-y-1 border-t border-stone-100 pt-3 text-xs text-stone-600">
                {c.notes.map((n, i) => <li key={i}><strong>{n.by}</strong> ({fmtDate(n.at)}): {n.text}</li>)}
              </ul>
            )}
            {noteFor === c.id && (
              <div className="mt-3 flex gap-2">
                <input className="p1-input" placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`case-note-input-${c.id}`} />
                <button onClick={() => addNote(c.id)} className="rounded-full bg-emerald-900 px-4 py-2 text-xs font-bold text-white" data-testid={`case-note-add-${c.id}`}>Add</button>
              </div>
            )}
          </div>
        ))}
        {cases.length === 0 && <p className="text-sm text-stone-400">No cases yet.</p>}
      </div>
    </div>
  );
}
